import { TERRA_RESOURCES } from "@healthos/shared";
import { z } from "zod";
import { ConsentScopeSchema } from "../consent.js";
export const ConnectionItem = z.object({
    provider: z.string(),
    connectedAt: z.string(),
    lastSyncAt: z.string().nullable(),
    // When the provider last reached us, whether or not it carried a reading.
    // lastSyncAt answers "is there new data"; this answers "is the pipe alive",
    // and a client that shows staleness needs the second one to say anything
    // true about a connected device that simply has nothing new to send.
    lastContactAt: z.string().nullable(),
    reauthRequired: z.boolean().optional(),
});
export const MobileCompanionConnection = z.object({
    connectedAt: z.string(),
    lastSeenAt: z.string(),
});
/**
 * A signal the member is actually receiving, and who actually sent it.
 *
 * Derived from their signals rather than from what a connected device claims it
 * can supply, so a scope appears here only once real data has arrived. Absence
 * is the honest answer for a connected-but-silent source.
 */
export const SignalCoverageItem = z.object({
    scope: ConsentScopeSchema,
    /** Real provider label from the signals themselves; null when unidentifiable. */
    sourceLabel: z.string().nullable(),
    lastSeenAt: z.string(),
});
export const GetConnectionsResponse = z.object({
    connections: z.array(ConnectionItem),
    mobileCompanion: MobileCompanionConnection.nullable(),
    coverage: z.array(SignalCoverageItem),
    /** Whether the Microsoft Teams/Outlook Calendar integration is enabled for this user's company. */
    msTeamsCalendarIntegrationEnabled: z.boolean(),
});
export const MobileCompanionConnectResponse = z.object({
    ok: z.literal(true),
});
export const GoogleCalendarSyncRequest = z.object({
    userId: z.string(),
    /** Refetch the analysis window rather than the routine day of lookback. */
    backfill: z.boolean().optional(),
});
export const GoogleCalendarSyncResponse = z.union([
    z.object({
        status: z.literal("ok"),
        eventCount: z.number().int().nonnegative(),
        signalCount: z.number().int().nonnegative(),
        syncedAt: z.string(),
        channelRenewed: z.boolean(),
        channelExpiresAt: z.string().nullable(),
    }),
    z.object({
        status: z.literal("not_connected"),
    }),
    z.object({
        status: z.literal("reauth_required"),
    }),
]);
export const MicrosoftCalendarSyncRequest = z.object({
    userId: z.string(),
});
// No channelRenewed/channelExpiresAt fields: v1 has no Graph push-notification
// subscription, only polling.
export const MicrosoftCalendarSyncResponse = z.union([
    z.object({
        status: z.literal("ok"),
        eventCount: z.number().int().nonnegative(),
        signalCount: z.number().int().nonnegative(),
        syncedAt: z.string(),
    }),
    z.object({
        status: z.literal("not_connected"),
    }),
    z.object({
        status: z.literal("reauth_required"),
    }),
    // The connection's own token is fine; Microsoft refused the Calendars.Read
    // call because the tenant-wide admin consent behind it is gone. Distinct
    // from reauth_required: reconnecting THIS user's calendar won't fix it.
    z.object({
        status: z.literal("consent_revoked"),
    }),
]);
export const ManualConnectionSyncResponse = z.union([
    z.object({
        status: z.literal("ok"),
        provider: z.union([z.literal("google_calendar"), z.literal("outlook_calendar")]),
        lastSyncAt: z.string(),
        eventCount: z.number().int().nonnegative(),
        signalCount: z.number().int().nonnegative(),
    }),
    z.object({
        status: z.literal("reauth_required"),
        provider: z.union([z.literal("google_calendar"), z.literal("outlook_calendar")]),
    }),
]);
export const ConnectionUrlResponse = z.object({
    url: z.string().url(),
});
export const TerraAuthQuery = z.object({
    resource: z.enum(TERRA_RESOURCES),
    /** Optional relative path to return to after the OAuth round-trip (e.g.
     *  "/onboarding"). Validated server-side; must be a same-origin path. */
    returnTo: z.string().optional(),
    /** Web returns to the SPA. Mobile and desktop return through their registered
     *  app schemes so the round-trip lands back in the native shell. "teams-popup"
     *  returns to the Teams wrapper's own `connector-auth-end.html` (see
     *  `returnUrlFor` in connections.ts) for providers whose consent screen cannot
     *  render inside the Teams tab iframe - the same reason Microsoft Calendar
     *  needs it, and just as true for Terra-backed providers like Google Health.
     *  "teams-browser" is for Terra resources whose consent screen isn't on an
     *  origin the Teams popup bridge trusts (most wearables - see
     *  `terraResourceSupportsTeamsPopup` in the web client's connection-config.ts):
     *  it opens in the system browser and returns via a Teams deep link instead. */
    target: z.enum(["web", "mobile", "desktop", "teams-popup", "teams-browser"]).optional(),
});
/** Shared query for connect endpoints that round-trip through OAuth and need to
 *  land back on a specific same-origin path (e.g. an onboarding step). */
export const ReturnToQuery = z.object({
    returnTo: z.string().optional(),
});
/** Query for the Google connect endpoint. Mirrors {@link ReturnToQuery} but adds
 *  a return target so native shells can complete OAuth in the system browser
 *  and deep-link back via their registered scheme. "teams-popup" returns to
 *  the Teams wrapper's `connector-auth-end.html` instead (see `returnUrlFor` in
 *  connections.ts) - Google's consent screen cannot render inside the Teams tab
 *  iframe either, same as Microsoft's and Terra's. */
export const GoogleConnectQuery = z.object({
    returnTo: z.string().optional(),
    target: z.enum(["web", "mobile", "desktop", "teams-popup"]).optional(),
});
/** Query for the Microsoft connect endpoint. Mirrors {@link GoogleConnectQuery}:
 *  a per-user authorization-code flow, same shape as Google Calendar. */
/** "calendar_and_presence" is the original, full-bundle option; "presence_only"
 *  omits Calendars.Read entirely. Only meaningful as a direct request on the
 *  admin-consent flow (MicrosoftConnectQuery.tier) - self-consent's scope is
 *  decided server-side by matching the connecting user's Entra group
 *  membership against ListMicrosoftSelfConsentGroupsResponse, not from a
 *  query param, since a member cannot choose their own tier. */
export const MicrosoftCalendarScopeTier = z.enum(["calendar_and_presence", "presence_only"]);
export const MicrosoftConnectQuery = z.object({
    returnTo: z.string().optional(),
    target: z.enum(["web", "mobile", "desktop", "teams-popup"]).optional(),
    // Admin-consent only: which scope tier the admin is approving. Ignored by
    // the self-consent /microsoft/connect route.
    tier: MicrosoftCalendarScopeTier.optional(),
});
/** Whether the CURRENT caller's own company has a tenant-wide Microsoft
 *  admin-consent grant on file - distinct from whether the caller has
 *  personally connected their own Outlook Calendar (see GetConnectionsResponse).
 *  `granted`/`grantedAt` describe a historical fact ("was this approved and
 *  when"), never a live-verified claim - see the calendar-permission-scope
 *  research thread for why that verification is structurally unreliable. */
export const MicrosoftAdminConsentStatusResponse = z.object({
    granted: z.boolean(),
    grantedAt: z.string().nullable(),
    scopeTier: MicrosoftCalendarScopeTier.nullable(),
});
/** One Entra security group a tenant admin told us they scoped via the
 *  self-consent script - a self-reported fact, not one HealthOS can
 *  verify. `groupId` is that group's own Entra Object ID, copied by the
 *  admin from Entra after running the script - HealthOS has no permission
 *  to resolve it to a display name. */
export const MicrosoftSelfConsentGroup = z.object({
    groupId: z.string(),
    scopeTier: MicrosoftCalendarScopeTier,
    addedAt: z.string(),
});
export const ListMicrosoftSelfConsentGroupsResponse = z.object({
    groups: z.array(MicrosoftSelfConsentGroup),
});
export const AddMicrosoftSelfConsentGroupRequest = z.object({
    groupId: z.string().min(1),
    scopeTier: MicrosoftCalendarScopeTier,
});
// Mobile SDK token (terra-react native HealthKit flow, distinct from web widget/OAuth)
export const TerraMobileTokenResponse = z.object({
    token: z.string(),
    devId: z.string(),
});
// Body sent by mobile after initConnection succeeds to record the connection in our DB
export const TerraAppleHealthConnectBody = z.object({
    terraUserId: z.string().min(1),
});
// Same shape for the Android device-health source. Kept as its own route and
// schema rather than a provider field on the Apple Health body, because
// installed app versions already POST that body without a provider and must
// keep recording Apple Health.
export const TerraHealthConnectConnectBody = z.object({
    terraUserId: z.string().min(1),
});
//# sourceMappingURL=connections.js.map